import * as React from 'react';
import { Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { AppointmentType } from '@prisma/client';
import { EmployeeToBusiness, LoadedUser, Option } from '../../interfaces';
import SelectInput from './SelectInput';
import DollarInput from './DollarInput';
import { getArtistOptionsForService } from '../../services/business';
import {
  CreateAptValues,
  DEFAULT_APT_LENGTH,
  DEFAULT_CONSULT_LENGTH,
  allowOverlappingOptions,
  depositPaymentMethodOptions,
  consultationLocationOptions,
  isQueueWalkIn,
  isUnassignedAppointmentAllowed,
  waiverOptions,
  waiverTooltip,
} from '../../utils/form/appointmentForm';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import { getBusinessTripName } from '../../helpers/businesses';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import { isAppointmentWaiverUpdatable } from '../../helpers/waivers';
import {
  getDepositPlacementSelectInput,
  getLengthOptions,
  isAppointmentDepositPaid,
  isDepositPlacementRequired,
  isWaiverSelectable,
} from '../../helpers/appointments';
import useUser from '../../hooks/global/useUser';
import useConfirm from '../../hooks/useConfirm';
import useBusinessEmployee from '../../hooks/employees/useBusinessEmployee';
import {
  applyArtistHourlyRateDefault,
  setVirtualFieldsFromEmployee,
} from '../../helpers/employees';
import { SelectedAppointment } from '../../types/appointments';
import CashDiscountFormRow, {
  toCashDiscountPrice,
} from '../payments/CashDiscountFormRow';
import PriceInputs from './PriceInputs';
import useBusinessOrganization from '../../hooks/businesses/useBusinessOrganization';

interface AppointmentDetailsProps {
  selectedAppointment: SelectedAppointment | null;
  disableDepositPaid?: boolean;
  // True when scheduling an appointment-backed request: the waiver choice was
  // prefilled from the persisted record and must not be re-defaulted
  hasPersistedWaiverChoice?: boolean;
}

export default function AppointmentDetails({
  selectedAppointment,
  disableDepositPaid,
  hasPersistedWaiverChoice,
}: AppointmentDetailsProps) {
  const confirm = useConfirm();
  const { user: loadedUser } = useUser<LoadedUser>();
  const { values, setFieldValue, touched, setFieldTouched } =
    useFormikContext<CreateAptValues>();
  const {
    hasMultipleBusinesses,
    businessOptions,
    getArtistOptionsForBusiness,
    getServiceOptionsForBusiness,
    getServiceForBusiness,
    getBusinessById,
  } = useBusinessOrganization();
  const appointmentType = values.appointmentType as AppointmentType;
  const currentBusinessId =
    values.businessId ||
    selectedAppointment?.businessId ||
    loadedUser?.business?.id;
  const isForeignAppointmentBusiness =
    !selectedAppointment?.businessId ||
    selectedAppointment.businessId !== loadedUser?.business?.id;
  const canChangeLocation =
    hasMultipleBusinesses && !isForeignAppointmentBusiness;
  const isOrgLocationChange =
    canChangeLocation &&
    !!values.businessId &&
    values.businessId !== loadedUser?.business?.id;

  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);

  const { data: waivers, isFetching: isFetchingWaivers } = useGetWaivers({
    businessId: currentBusinessId,
  });
  const showWaiverSelect = isWaiverSelectable({
    waivers,
    hasWaiversFeature,
    appointmentType,
    serviceId: values.serviceId ? Number(values.serviceId) : undefined,
  });

  // No startDateTime = not scheduled yet, so the choice is still open;
  // only past appointments and signed waivers are locked
  const isWaiversDisabled =
    !!selectedAppointment &&
    ((!!selectedAppointment.startDateTime &&
      !isAppointmentWaiverUpdatable(selectedAppointment.startDateTime)) ||
      selectedAppointment?.waiverRequest?.status === 'SIGNED');

  const isConsultation = appointmentType === 'CONSULTATION';
  const isWalkIn = appointmentType === 'WALK_IN';
  const isWalkInOrConsultation = isWalkIn || appointmentType === 'CONSULTATION';
  const showPrice = !isConsultation && !values.isTouchUp;

  const { employee, employeeHasVirtualUrl } = useBusinessEmployee(
    values.employeeId,
    currentBusinessId,
  );

  const setVirtualFields = React.useCallback(
    (
      passedEmployee:
        | Pick<
            EmployeeToBusiness,
            'defaultVirtualProvider' | 'consultationPermanentVirtualLink'
          >
        | undefined,
      isVirtual: boolean,
    ) => setVirtualFieldsFromEmployee(setFieldValue, passedEmployee, isVirtual),
    [setFieldValue],
  );

  const serviceOptions = React.useMemo(() => {
    if (isOrgLocationChange) {
      return getServiceOptionsForBusiness(values.businessId);
    }

    const options: Option[] = [{ value: '0', label: 'Tattoo' }];
    if (!loadedUser?.business?.services) {
      return options;
    }
    loadedUser.business.services.forEach(service => {
      const existing = options.find(
        option => option.value === String(service.serviceId),
      );
      if (!existing) {
        options.push({
          value: String(service.serviceId),
          label: service.serviceName,
        });
      }
    });
    return options;
  }, [
    getServiceOptionsForBusiness,
    isOrgLocationChange,
    loadedUser?.business?.services,
    values.businessId,
  ]);

  const tripOptions = React.useMemo(() => {
    const options: Option[] = [{ value: '', label: 'No Trip (Home Studio)' }];
    if (!loadedUser?.business?.businessTrips) {
      return options;
    }
    loadedUser.business.businessTrips.forEach(trip => {
      const existing = options.find(option => option.value === trip.tripId);
      if (!existing) {
        options.push({
          value: trip.tripId,
          label: getBusinessTripName(trip),
        });
      }
    });
    return options;
  }, [loadedUser?.business?.businessTrips]);

  const appointmentTypeOptions = React.useMemo<Option[]>(
    () => [
      { value: 'NEW_APPOINTMENT', label: 'Appointment' },
      { value: 'CONSULTATION', label: 'Consultation' },
    ],
    [],
  );

  React.useEffect(() => {
    // Default for new appointments only — an edit must keep the persisted
    // choice instead of overwriting it once waivers load
    if (selectedAppointment || hasPersistedWaiverChoice) return;
    setFieldValue('requestWaiver', showWaiverSelect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWaiverSelect]);

  React.useEffect(() => {
    if (isOrgLocationChange && !showWaiverSelect && values.requestWaiver) {
      setFieldValue('requestWaiver', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgLocationChange, showWaiverSelect]);

  const service = isOrgLocationChange
    ? getServiceForBusiness(values.businessId, values.serviceId)
    : loadedUser?.business.services?.find(
        s => String(s.serviceId) === String(values.serviceId),
      );

  const isTattoo = !service || service.serviceId === 0;
  const isQueueWalkInSubmission = isQueueWalkIn({
    isWalkIn,
    user: loadedUser,
  });
  const isArtistOptional =
    isQueueWalkInSubmission ||
    isUnassignedAppointmentAllowed({
      isWalkIn,
      user: loadedUser,
      isEditingExisting: !!selectedAppointment,
    });

  const appointmentLengthOptions = getLengthOptions({
    user: loadedUser,
    appointmentType: values.appointmentType as AppointmentType,
    serviceId: values.serviceId ? Number(values.serviceId) : undefined,
    currentLength: values.appointmentLengthInMinutes
      ? Number(values.appointmentLengthInMinutes)
      : undefined,
    businessId: currentBusinessId,
  });

  const depositPaidOptions = [
    { value: false, label: 'No' },
    { value: true, label: 'Yes' },
  ];

  const { artistLabel, artistPlaceholder } = React.useMemo(() => {
    const suffix = !isArtistOptional || !selectedAppointment ? ' *' : '';
    if (!isTattoo) {
      return {
        artistLabel: `Provider${suffix}`,
        artistPlaceholder: 'Select service provider',
      };
    }
    return {
      artistLabel: `Artist${suffix}`,
      artistPlaceholder: 'Select artist',
    };
  }, [isArtistOptional, isTattoo, selectedAppointment]);

  // Only run on initial load or if loadedUser changes
  React.useEffect(() => {
    if (
      loadedUser?.business.defaultTattooDeposit &&
      !isWalkInOrConsultation &&
      !selectedAppointment &&
      !values.isTouchUp
    ) {
      setFieldValue('depositPrice', loadedUser.business.defaultTattooDeposit);
      setFieldValue('price', loadedUser.business.defaultTattooDeposit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedUser]);

  const artistOptions = React.useMemo(() => {
    if (isOrgLocationChange) {
      return getArtistOptionsForBusiness(values.businessId);
    }

    return getArtistOptionsForService({
      serviceId: values.serviceId,
      user: loadedUser,
      addQueueOption: !selectedAppointment && isQueueWalkInSubmission,
    });
  }, [
    getArtistOptionsForBusiness,
    isOrgLocationChange,
    isQueueWalkInSubmission,
    loadedUser,
    selectedAppointment,
    values.businessId,
    values.serviceId,
  ]);

  const confirmDateReset = async (check = false) => {
    const requireReset =
      check &&
      !isWalkIn &&
      (values.startDateTime || values.additionalAppointments?.length);
    if (requireReset) {
      await confirm({
        dismissible: true,
        title: 'Date & Time Reset',
        message: `Your selected date & time will be reset. Please re-select a new date & time for this ${
          isConsultation ? 'consultation' : 'appointment'
        }.`,
        isSmall: true,
        positiveText: 'Okay',
        negativeText: '',
      });
      setFieldValue('additionalAppointments', []);
      setFieldValue('startDateTime', '');
    }
  };

  const onChangeLocation = async (name: string, value: string) => {
    const previousBusinessId = values.businessId || currentBusinessId;
    setFieldValue(name, value);
    if (value && value !== previousBusinessId) {
      const newBusinessHasEmployee =
        !!values.employeeId &&
        !!getBusinessById(value)?.employeesToBusinesses.some(
          etb => etb.employeeId === values.employeeId,
        );
      if (!newBusinessHasEmployee) {
        setFieldValue('employeeId', '');
        await confirmDateReset(true);
      }
      if (values.serviceId && values.serviceId !== '0') {
        setFieldValue('serviceId', '0');
      }
      if (values.businessTripId) {
        setFieldValue('businessTripId', '');
      }
    }
  };

  const onChangeService = async (name: string, value: string) => {
    setFieldValue(name, value);
    if (value !== values.serviceId) {
      const newService = isOrgLocationChange
        ? getServiceForBusiness(values.businessId, value)
        : loadedUser?.business.services?.find(
            s => s.serviceId === Number(value),
          );
      const isNewServiceTattoo = !newService || newService?.serviceId === 0;

      await confirmDateReset(true);

      const newLengthOptions = isOrgLocationChange
        ? undefined
        : getLengthOptions({
            user: loadedUser,
            appointmentType: values.appointmentType as AppointmentType,
            serviceId: newService ? newService.serviceId : undefined,
          });

      const isCurrentLengthInvalid = !newLengthOptions?.some(
        length =>
          Number(length.value) === Number(values.appointmentLengthInMinutes),
      );

      if (isCurrentLengthInvalid) {
        setFieldValue('appointmentLengthInMinutes', '');
      }

      const newArtistOptions = isOrgLocationChange
        ? getArtistOptionsForBusiness(values.businessId)
        : getArtistOptionsForService({
            serviceId: value,
            user: loadedUser,
          });
      const isCurrentArtistInvalid =
        values.employeeId &&
        !newArtistOptions?.some(artist => artist.value === values.employeeId);
      if (isCurrentArtistInvalid) {
        setFieldValue('employeeId', '');
      }

      if (
        !touched.appointmentLengthInMinutes &&
        !selectedAppointment &&
        appointmentType === 'CONSULTATION'
      ) {
        setFieldValue('appointmentLengthInMinutes', DEFAULT_CONSULT_LENGTH);
      }

      if (newService && !isNewServiceTattoo && !selectedAppointment) {
        if (!touched.appointmentLengthInMinutes && !isConsultation) {
          setFieldValue(
            'appointmentLengthInMinutes',
            String(newService.defaultDurationInMinutes),
          );
        }

        if (!touched.price && !isConsultation) {
          setFieldValue('price', newService.defaultPrice);
        }

        if (!touched.depositPrice && !isWalkInOrConsultation) {
          setFieldValue('depositPrice', newService.defaultDepositPrice || 0);
        }
      } else if (isNewServiceTattoo && !selectedAppointment) {
        if (!touched.price && !isConsultation) {
          setFieldValue('price', loadedUser?.business.defaultTattooDeposit);
        }
        if (!touched.depositPrice && !isWalkInOrConsultation) {
          setFieldValue(
            'depositPrice',
            loadedUser?.business.defaultTattooDeposit,
          );
        }
        if (!touched.appointmentLengthInMinutes) {
          setFieldValue(
            'appointmentLengthInMinutes',
            isConsultation ? DEFAULT_CONSULT_LENGTH : DEFAULT_APT_LENGTH,
          );
        }
      }
    }
  };

  const onChangeAppointmentType = (name: string, value: string) => {
    setFieldValue(name, value);
    if (value === 'CONSULTATION') {
      setFieldValue('appointmentLengthInMinutes', DEFAULT_CONSULT_LENGTH);
      setFieldValue('price', 0);
      setFieldValue('depositPrice', 0);
      setFieldValue('priceType', 'FIXED');
      setFieldValue('requestWaiver', false);

      setVirtualFields(employee, !!employee?.consultationVirtualByDefault);
    } else {
      setFieldValue('appointmentLengthInMinutes', DEFAULT_APT_LENGTH);
      setFieldValue('price', loadedUser?.business.defaultTattooDeposit ?? 0);
      setFieldValue(
        'depositPrice',
        loadedUser?.business.defaultTattooDeposit ?? 0,
      );
      setFieldValue('priceType', 'FIXED');
      setVirtualFields(employee, false);
    }
  };

  const onChangeEmployee = (name: string, value: string) => {
    if (!value && isQueueWalkInSubmission) {
      setFieldValue(name, '');
      if (isConsultation && !touched.isVirtual) {
        setVirtualFields(undefined, false);
      }
      return;
    }

    const employeeToBusiness = loadedUser?.business.employees.find(
      e => e.employeeId === value,
    );
    const orgEmployeeMapping = !employeeToBusiness
      ? getBusinessById(values.businessId)?.employeesToBusinesses.find(
          etb => etb.employeeId === value,
        )
      : null;

    // Org location moves can select artists that only exist at the other shop.
    if (!employeeToBusiness && !orgEmployeeMapping) return;

    setFieldValue(name, value);
    if (showPrice) {
      applyArtistHourlyRateDefault(
        employeeToBusiness?.defaultHourlyRate ??
          orgEmployeeMapping?.defaultHourlyRate,
        touched,
        setFieldValue,
      );
    }

    if (isConsultation && !touched.isVirtual) {
      const virtualDefaultsEmployee =
        employeeToBusiness ?? orgEmployeeMapping?.employee;
      setVirtualFields(
        virtualDefaultsEmployee,
        !!virtualDefaultsEmployee?.consultationVirtualByDefault,
      );
    }

    setTimeout(() => {
      setFieldTouched(name, true);
    }, 0);
  };

  const title = isConsultation ? 'Consultation Details' : 'Appointment Details';
  const priceDisabled =
    !!selectedAppointment?.checkoutSessionActive ||
    !!selectedAppointment?.checkoutSessionCompleted;
  const canConvertAppointmentType =
    appointmentType === 'NEW_APPOINTMENT' || appointmentType === 'CONSULTATION';
  const showAppointmentTypeSelect =
    canConvertAppointmentType &&
    (!!values.isBookingLink || !!selectedAppointment);
  const isAppointmentTypeDisabled =
    !!selectedAppointment &&
    (isAppointmentDepositPaid(selectedAppointment) ||
      !!selectedAppointment.checkoutSessionActive ||
      !!selectedAppointment.checkoutSessionCompleted ||
      selectedAppointment.waiverRequest?.status === 'SIGNED');

  return (
    <div className="input-group appointment-details">
      <Typography variant="h2">{title}</Typography>
      <div className="inputs">
        <div className="input-row">
          <SelectInput
            values={values}
            setFieldValue={onChangeService}
            name="serviceId"
            label="Service"
            options={serviceOptions}
            placeholder="Select a service"
            disabled={false}
            hidden={serviceOptions.length <= 1}
          />
        </div>
        {showAppointmentTypeSelect && (
          <div className="input-row">
            <SelectInput
              values={values}
              setFieldValue={onChangeAppointmentType}
              name="appointmentType"
              label="Appointment Type *"
              options={appointmentTypeOptions}
              placeholder="Select an appointment type"
              disabled={isAppointmentTypeDisabled}
            />
          </div>
        )}
        {canChangeLocation &&
          !!selectedAppointment &&
          !values.businessTripId &&
          !selectedAppointment.tripCity && (
            <div className="input-row">
              <SelectInput
                values={values}
                setFieldValue={onChangeLocation}
                name="businessId"
                label="Location"
                options={businessOptions}
                placeholder="Select a location"
                disabled={false}
              />
            </div>
          )}
        {artistOptions && (
          <div className="input-row">
            <SelectInput
              values={values}
              setFieldValue={onChangeEmployee}
              name="employeeId"
              label={artistLabel}
              options={artistOptions}
              placeholder={artistPlaceholder}
              disabled={false}
            />
          </div>
        )}
        {appointmentLengthOptions && (
          <div className="input-row">
            <SelectInput
              values={values}
              setFieldValue={async (name, value) => {
                const isIncreased =
                  parseInt(value || '0', 10) >
                  parseInt(values.appointmentLengthInMinutes || '0', 10);
                await confirmDateReset(isIncreased);
                setFieldValue(name, value);
              }}
              name="appointmentLengthInMinutes"
              label={`Length${isQueueWalkInSubmission ? '' : ' *'}`}
              options={appointmentLengthOptions}
              placeholder="Select duration"
              disabled={false}
            />
          </div>
        )}
        {!isWalkInOrConsultation && !values.isTouchUp && (
          <>
            <div className="input-row">
              <DollarInput
                name="depositPrice"
                label="Deposit Amount *"
                placeholder="Enter Amount"
                values={values}
                setFieldValue={setFieldValue}
                disabled={
                  selectedAppointment?.status === 'CONFIRMED' ||
                  !!selectedAppointment?.depositPaidOn ||
                  !!selectedAppointment?.checkoutSessionActive ||
                  !!selectedAppointment?.checkoutSessionCompleted
                }
                hidden={false}
                setFieldTouched={setFieldTouched}
              />
            </div>
            <CashDiscountFormRow
              price={{
                type: 'FIXED',
                amount: values.depositPrice || 0,
              }}
            />
            <div className="input-row">
              <SelectInput
                name="depositPaid"
                label="Deposit Already Paid"
                placeholder="Has the deposit been paid?"
                values={{
                  ...values,
                  depositPaid:
                    (values.depositPrice ?? 0) > 0 ? values.depositPaid : false,
                }}
                setFieldValue={(name, value) => {
                  setFieldValue(name, value);
                  // reset payment method when deposit paid is set to false
                  if (!value) {
                    setFieldValue('depositPaymentMethod', undefined);
                  } else if (!values.depositPaymentMethod) {
                    // default to CASH when deposit paid is set to true
                    setFieldValue('depositPaymentMethod', 'CASH');
                  }
                }}
                disabled={
                  selectedAppointment?.status === 'CONFIRMED' ||
                  !!selectedAppointment?.checkoutSessionActive ||
                  !!selectedAppointment?.checkoutSessionCompleted ||
                  (values.depositPrice ?? 0) <= 0 ||
                  disableDepositPaid ||
                  (!!selectedAppointment &&
                    isAppointmentDepositPaid(selectedAppointment))
                }
                hidden={false}
                options={depositPaidOptions}
              />
            </div>
            {values.depositPaid &&
              (values.depositPrice ?? 0) > 0 &&
              (!selectedAppointment ||
                !isAppointmentDepositPaid(selectedAppointment)) && (
                <div className="input-row">
                  <SelectInput
                    name="depositPaymentMethod"
                    label="Payment Method"
                    placeholder="Select payment method"
                    values={values}
                    setFieldValue={setFieldValue}
                    disabled={!!disableDepositPaid}
                    hidden={false}
                    options={depositPaymentMethodOptions}
                  />
                </div>
              )}
          </>
        )}
        {showPrice && (
          <>
            <PriceInputs disabled={priceDisabled} />
            <CashDiscountFormRow price={toCashDiscountPrice(values)} />
          </>
        )}
        {showWaiverSelect && (
          <div className="input-row">
            <SelectInput
              name="requestWaiver"
              label="Waiver"
              placeholder="Select waiver option"
              options={waiverOptions}
              disabled={isFetchingWaivers || isWaiversDisabled}
              values={values}
              setFieldValue={setFieldValue}
              tooltip={waiverTooltip}
            />
          </div>
        )}

        {isConsultation && (
          <>
            {employeeHasVirtualUrl && (
              <div className="input-row">
                <SelectInput
                  name="isVirtual"
                  label="Location"
                  placeholder="Select location type"
                  options={consultationLocationOptions}
                  disabled={false}
                  values={values}
                  setFieldValue={(name, value) => {
                    setVirtualFields(employee, value as unknown as boolean);
                    setFieldTouched('isVirtual', true);
                  }}
                  tooltip="Location defaults to the selected artist's preference. You can override it here."
                />
              </div>
            )}
            <div className="input-row">
              <SelectInput
                name="allowOverlapping"
                label="Double Book"
                placeholder="Select an option"
                options={allowOverlappingOptions}
                disabled={!isConsultation}
                values={values}
                setFieldValue={setFieldValue}
              />
            </div>
          </>
        )}

        {!isOrgLocationChange && tripOptions && tripOptions.length > 1 && (
          <div className="input-row">
            <SelectInput
              name="businessTripId"
              label="Trip"
              placeholder="Select a trip"
              options={tripOptions}
              disabled={false}
              values={values}
              setFieldValue={setFieldValue}
            />
          </div>
        )}

        {isDepositPlacementRequired(
          values.additionalAppointments ?? [],
          values.depositPrice,
        ) && (
          <div className="input-row">
            <SelectInput
              values={values}
              setFieldValue={setFieldValue}
              {...getDepositPlacementSelectInput({
                required: true,
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
